import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, fmtINR, formatApiError, ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, UsersThree, Pencil, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_FORM = {
  code: "", name: "", centre_type: "", project_name: "",
  state: "", district: "", city: "", address: "",
  contact_person: "", contact: "", email: "",
  monthly_budget: 0, annual_budget: 0,
  status: "Active", company_id: "",
};

const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-800 border-emerald-300",
  Inactive: "bg-zinc-100 text-zinc-600 border-zinc-300",
  Deleted: "bg-rose-50 text-rose-700 border-rose-300 line-through",
};

// ---------- Client-side validation ----------
function validateCentre(f, isEdit) {
  const errors = {};
  if (!f.name?.trim()) errors.name = "Name is required";
  if (!isEdit) {
    if (!f.code?.trim()) errors.code = "Code is required";
    else if (!/^[A-Z0-9-]{2,20}$/.test(f.code.trim()))
      errors.code = "Code: uppercase letters, digits, dashes only (2–20 chars)";
  }
  if (f.contact && !/^[+\d\s()-]{6,20}$/.test(f.contact))
    errors.contact = "Phone looks invalid";
  if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email))
    errors.email = "Invalid email";
  if (Number(f.monthly_budget) < 0 || Number(f.annual_budget) < 0)
    errors.monthly_budget = "Budget cannot be negative";
  return errors;
}

export default function Centres() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  // Create + edit dialog share state
  const [dialog, setDialog] = useState(null); // { mode: 'create' | 'edit', centre?: obj }
  const [f, setF] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Users-assignment dialog
  const [usersFor, setUsersFor] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [assignedIds, setAssignedIds] = useState([]);
  const [savingUsers, setSavingUsers] = useState(false);

  const canManage = user?.role === "admin";
  const [projectFilter, setProjectFilter] = useState("all");

  const load = () =>
    api.get(`/centres?include_deleted=${showDeleted ? "true" : "false"}`)
       .then((r) => setItems(r.data));
  useEffect(() => {
    if (user && user.role === "centre_manager") return;
    load();
    api.get("/projects").then((r) => setProjectsList(r.data)).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted, user]);

  if (user && user.role === "centre_manager") {
    return <Navigate to="/dashboard" replace />;
  }

  // ---------- Create / Edit ----------
  const openCreate = () => {
    setDialog({ mode: "create" });
    setF(EMPTY_FORM);
    setErrors({});
  };
  const openEdit = (c) => {
    setDialog({ mode: "edit", centre: c });
    setF({
      ...EMPTY_FORM, ...c,
      monthly_budget: c.monthly_budget || 0,
      annual_budget: c.annual_budget || 0,
    });
    setErrors({});
  };

  const submit = async () => {
    const isEdit = dialog?.mode === "edit";
    const err = validateCentre(f, isEdit);
    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...f,
        monthly_budget: Number(f.monthly_budget) || 0,
        annual_budget: Number(f.annual_budget) || 0,
        company_id: f.company_id || null,
      };
      if (isEdit) {
        // Backend ignores immutable `code` on PATCH but strip anyway
        delete payload.code;
        delete payload.id;
        await api.patch(`/centres/${dialog.centre.id}`, payload);
        toast.success(`Updated ${dialog.centre.code}`);
      } else {
        await api.post("/centres", payload);
        toast.success(`Created ${payload.code}`);
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // ---------- Restore (soft-deleted) ----------
  const restore = async (c) => {
    try {
      await api.post(`/centres/${c.id}/restore`);
      toast.success(`${c.code} restored to Active`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // ---------- Users assignment ----------
  const openUsers = async (centre) => {
    setUsersFor(centre);
    try {
      const [uAll, cList] = await Promise.all([
        api.get("/users"),
        api.get(`/centres/${centre.id}/users`),
      ]);
      setAllUsers(uAll.data);
      setAssignedIds(cList.data.filter((u) => u.assigned_to_this_centre && !u.wildcard).map((u) => u.id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setUsersFor(null);
    }
  };
  const toggleAssigned = (id) =>
    setAssignedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const saveUsers = async () => {
    setSavingUsers(true);
    try {
      await api.post(`/centres/${usersFor.id}/users`, { user_ids: assignedIds });
      toast.success(`Users updated for ${usersFor.code}`);
      setUsersFor(null);
      setAssignedIds([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingUsers(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="centres-root">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Master</div>
          <h1 className="font-heading text-4xl mt-1 tracking-tight">Centres</h1>
          <p className="text-sm text-zinc-500 mt-1">Grouped project-wise. Every centre rolls up to its project.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2" data-testid="project-filter">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Project</span>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="rounded-sm h-9 w-52" data-testid="project-filter-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {Array.from(new Set(items.map((c) => c.project_name || "Unassigned"))).sort().map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {canManage && (
            <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="toggle-show-deleted">
              <Checkbox checked={showDeleted} onCheckedChange={(v) => setShowDeleted(!!v)} />
              <span className="uppercase tracking-widest">Show deleted</span>
            </label>
          )}
          {canManage && (
            <Button onClick={openCreate} data-testid="new-centre-btn"
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10 gap-2">
              <Plus size={14} weight="bold" /> New Centre
            </Button>
          )}
        </div>
      </div>

      {(() => {
        const filtered = projectFilter === "all"
          ? items
          : items.filter((c) => (c.project_name || "Unassigned") === projectFilter);
        const groups = filtered.reduce((acc, c) => {
          const key = c.project_name || "Unassigned";
          (acc[key] = acc[key] || []).push(c);
          return acc;
        }, {});
        const projectKeys = Object.keys(groups).sort((a, b) => {
          if (a === "Unassigned") return 1;
          if (b === "Unassigned") return -1;
          return a.localeCompare(b);
        });
        return (
      <div className="space-y-6">
        {filtered.length === 0 && (
          <div className="bg-white border border-zinc-200 p-12 text-center text-sm text-zinc-500" data-testid="centres-empty">
            No centres to display.
          </div>
        )}
        {projectKeys.map((proj) => {
          const rows = groups[proj];
          const totalBudget = rows.reduce((s, c) => s + (c.annual_budget || 0), 0);
          return (
            <div key={proj} className="bg-white border border-zinc-200 overflow-x-auto"
              data-testid={`project-group-${proj.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Project</div>
                  <div className="font-heading text-lg mt-0.5">{proj}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{rows.length} centre{rows.length === 1 ? "" : "s"} · annual budget</div>
                  <div className="font-heading text-base tabular-nums mt-0.5">{fmtINR(totalBudget)}</div>
                </div>
              </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4">Code</th>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Company</th>
              <th className="text-left p-4">Contact Person</th>
              <th className="text-left p-4">Phone</th>
              <th className="text-left p-4">City</th>
              <th className="text-right p-4">Annual Budget</th>
              <th className="text-left p-4">Status</th>
              {canManage && <th className="text-right p-4">Actions</th>}
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {rows.map((c) => {
              const isDeleted = c.status === "Deleted";
              return (
                <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50"
                  data-testid={`centre-row-${c.code}`}>
                  <td className="p-4 font-mono text-xs">{c.code}</td>
                  <td className="p-4 font-medium">{c.name}</td>
                  <td className="p-4 text-zinc-600" data-testid={`centre-company-${c.code}`}>
                    {companies.find((co) => co.id === c.company_id)?.name || "—"}
                  </td>
                  <td className="p-4 text-zinc-600">{c.contact_person || "—"}</td>
                  <td className="p-4 text-zinc-600">{c.contact || "—"}</td>
                  <td className="p-4 text-zinc-600">{c.city || "—"}</td>
                  <td className="p-4 text-right">{fmtINR(c.annual_budget)}</td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${STATUS_STYLES[c.status] || STATUS_STYLES.Inactive}`}>
                      {c.status || "—"}
                    </span>
                  </td>
                  {canManage && (
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-3 items-center">
                        {isDeleted ? (
                          <button onClick={() => restore(c)}
                            className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900"
                            data-testid={`restore-centre-${c.code}`}>
                            <ArrowCounterClockwise size={12} /> Restore
                          </button>
                        ) : (
                          <>
                            <button onClick={() => openUsers(c)}
                              className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
                              data-testid={`centre-users-btn-${c.code}`}>
                              <UsersThree size={12} /> Users
                            </button>
                            <button onClick={() => openEdit(c)}
                              className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
                              data-testid={`edit-centre-${c.code}`}>
                              <Pencil size={12} /> Edit
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
          );
        })}
      </div>
        );
      })()}

      {/* Create / Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-white rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {dialog?.mode === "edit" ? `Edit Centre · ${dialog.centre?.code}` : "Create Centre"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {dialog?.mode === "edit"
                ? "Update centre details. Code is immutable to preserve audit history."
                : "Set up a new centre with contact and budget information."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[55vh] overflow-y-auto pr-2">
            <FormField label="Code" error={errors.code} required>
              <Input value={f.code}
                disabled={dialog?.mode === "edit"}
                onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
                placeholder="e.g. PUN-01"
                className="rounded-sm h-10 mt-2" data-testid="centre-code" />
            </FormField>
            <FormField label="Name" error={errors.name} required>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                className="rounded-sm h-10 mt-2" data-testid="centre-name" />
            </FormField>
            <FormField label="Type">
              <Input value={f.centre_type} onChange={(e) => setF({ ...f, centre_type: e.target.value })} className="rounded-sm h-10 mt-2" />
            </FormField>
            <FormField label="Project">
              <Select value={f.project_name || "__none__"}
                onValueChange={(v) => setF({ ...f, project_name: v === "__none__" ? "" : v })}>
                <SelectTrigger className="rounded-sm h-10 mt-2" data-testid="centre-project-select">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {projectsList.filter((p) => p.status === "Active").map((p) => (
                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Company">
              <Select value={f.company_id || "__none__"}
                onValueChange={(v) => setF({ ...f, company_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="rounded-sm h-10 mt-2" data-testid="centre-company-select">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {companies.map((co) => (
                    <SelectItem key={co.id} value={co.id}>{co.name} ({co.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="State">
              <Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className="rounded-sm h-10 mt-2" />
            </FormField>
            <FormField label="City">
              <Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="rounded-sm h-10 mt-2" />
            </FormField>
            <FormField label="Contact Person">
              <Input value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })}
                className="rounded-sm h-10 mt-2" data-testid="centre-contact-person" />
            </FormField>
            <FormField label="Phone" error={errors.contact}>
              <Input value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })}
                className="rounded-sm h-10 mt-2" data-testid="centre-phone" />
            </FormField>
            <FormField label="Email" error={errors.email}>
              <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                className="rounded-sm h-10 mt-2" />
            </FormField>
            <FormField label="Status">
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger className="rounded-sm h-10 mt-2" data-testid="centre-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Monthly Budget (₹)" error={errors.monthly_budget}>
              <Input type="number" value={f.monthly_budget}
                onChange={(e) => setF({ ...f, monthly_budget: e.target.value })}
                className="rounded-sm h-10 mt-2 tabular-nums text-right" />
            </FormField>
            <FormField label="Annual Budget (₹)">
              <Input type="number" value={f.annual_budget}
                onChange={(e) => setF({ ...f, annual_budget: e.target.value })}
                className="rounded-sm h-10 mt-2 tabular-nums text-right" />
            </FormField>
            <div className="col-span-2">
              <FormField label="Address">
                <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })}
                  className="rounded-sm h-10 mt-2" />
              </FormField>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="save-centre"
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">
              {saving ? "Saving…" : dialog?.mode === "edit" ? "Save Changes" : "Create Centre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Users assignment */}
      <Dialog open={!!usersFor} onOpenChange={(v) => !v && setUsersFor(null)}>
        <DialogContent className="bg-white rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Assign Users · {usersFor?.code} {usersFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {allUsers.filter((u) => u.role !== "admin" && !u.all_centres).length === 0 && (
              <p className="text-xs text-zinc-500">No assignable users. Wildcard/admin users already have access.</p>
            )}
            {allUsers.filter((u) => u.role !== "admin" && !u.all_centres).map((u) => (
              <label key={u.id}
                className="flex items-center justify-between gap-3 p-2 border border-zinc-200 rounded-sm hover:bg-zinc-50"
                data-testid={`centre-user-toggle-${u.email}`}>
                <div>
                  <div className="text-sm font-medium">{u.name}</div>
                  <div className="text-xs text-zinc-500">{u.email} · {ROLES[u.role] || u.role}</div>
                </div>
                <Checkbox checked={assignedIds.includes(u.id)} onCheckedChange={() => toggleAssigned(u.id)} />
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUsersFor(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveUsers} disabled={savingUsers} data-testid="save-centre-users"
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">
              {savingUsers ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Little form-field wrapper with inline error text ----------
function FormField({ label, error, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest">
        {label}
        {required && <span className="text-rose-600 ml-0.5">*</span>}
      </Label>
      {children}
      {error && <p className="text-[10px] text-rose-600 mt-1">{error}</p>}
    </div>
  );
}
